import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface AuthUser {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	fullName: string;
	role: { key: string; label: string };
	permissions: string[];
	mfaEnabled: boolean;
	lang: string;
	timezone: string;
}

export interface MfaMethod {
	id: string;
	type: "totp" | "sms" | "email" | "whatsapp";
	isDefault: boolean;
	label: string | null;
}

interface AuthState {
	user: AuthUser | null;
	accessToken: string | null;
	refreshToken: string | null;
	isAuthenticated: boolean;
	/** Identifiant en attente de vérification 2FA (étape /login → /login/2fa) */
	pending2FA: { email: string; token: string; methods: MfaMethod[] } | null;

	setPending2FA: (data: {
		email: string;
		token: string;
		methods: MfaMethod[];
	}) => void;
	setSession: (data: {
		user: AuthUser;
		accessToken: string;
		refreshToken: string;
	}) => void;
	/** Met à jour le couple de tokens après un refresh silencieux (conserve le user). */
	setTokens: (data: { accessToken: string; refreshToken: string }) => void;
	cancelPending: () => void;
	logout: () => void;
	setUser: (user: AuthUser) => void;
}

export const useAuthStore = create<AuthState>()(
	persist(
		(set) => ({
			user: null,
			accessToken: null,
			refreshToken: null,
			isAuthenticated: false,
			pending2FA: null,

			setPending2FA: (data) =>
				set({ pending2FA: data, isAuthenticated: false }),

			setSession: ({ user, accessToken, refreshToken }) =>
				set({
					user,
					accessToken,
					refreshToken,
					isAuthenticated: true,
					pending2FA: null,
				}),

			setTokens: ({ accessToken, refreshToken }) =>
				set({ accessToken, refreshToken, isAuthenticated: true }),

			cancelPending: () => set({ pending2FA: null }),

			logout: () =>
				set({
					user: null,
					accessToken: null,
					refreshToken: null,
					isAuthenticated: false,
					pending2FA: null,
				}),

			setUser: (user) => set({ user }),
		}),
		{
			name: "akwaba-auth",
			partialize: (state) => ({
				user: state.user,
				accessToken: state.accessToken,
				refreshToken: state.refreshToken,
				isAuthenticated: state.isAuthenticated,
			}),
		},
	),
);
